import { CarouselLinkWrapper, CarouselSectionTypes, CarouselTiledCards } from '@dfh-corp-sites/ui'
import React, { useMemo } from 'react'

import { useSanityRoleBasedContent } from '../../../lib/sanity'
import SanityLink from '../../global/SanityLink'
import { CarouselSectionProps } from './types'
import { transformLink } from './utils'

function CarouselSectionEducation({
  educationCarouselDeck,
  title,
  subtitle,
  text,
  ctas,
  roleBasedContent,
}: Pick<
  CarouselSectionProps,
  'educationCarouselDeck' | 'title' | 'subtitle' | 'text' | 'ctas' | 'roleBasedContent'
>) {
  const { shouldDisplayContent } = useSanityRoleBasedContent()

  const shouldDisplay = useMemo(() => {
    if (!roleBasedContent) return true
    return shouldDisplayContent({ roleBasedContent })
  }, [roleBasedContent, shouldDisplayContent])

  const slides = useMemo(() => {
    if (!educationCarouselDeck?.educationSlides?.length) {
      return []
    }

    return educationCarouselDeck.educationSlides
      .filter((slide) => {
        if (slide === null) return false

        if (slide.roleBasedContent) {
          return shouldDisplayContent({ roleBasedContent: slide.roleBasedContent })
        }

        return true
      })
      .map((slide, index) => {
        const hasLink = slide.link?.target?.[0]
        return {
          ...slide,
          _key: slide._key ?? String(index),
          type: slide.type ?? CarouselSectionTypes.Education,
          linkWrapper: hasLink
            ? ((({ children }) => (
                <SanityLink
                  to={slide.link}
                  className="text-xl block h-full rounded-lg transition-shadow hover:shadow-product-card focus-visible:shadow-product-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-navy focus-visible:ring-offset-2"
                >
                  {children}
                </SanityLink>
              )) as CarouselLinkWrapper)
            : null,
        }
      })
  }, [educationCarouselDeck, shouldDisplayContent])

  if (!shouldDisplay) {
    return null
  }

  const processedCtas = ctas?.map((cta, index) => transformLink(cta, String(index)))

  return (
    <section className="w-full">
      <CarouselTiledCards
        sliderType={CarouselSectionTypes.Education}
        slides={slides}
        title={title}
        subtitle={subtitle}
        text={text}
        ctas={processedCtas}
        className="[&_.swiper-slide]:h-52 [&_.swiper-slide>div]:h-full"
      />
    </section>
  )
}

export default CarouselSectionEducation
